//src/components/StandaloneSignUpForm.tsx

import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { signUpUser, confirmSignUpUser, signInUser, resendVerificationCode } from "../auth/cognitoAuth";
import { signOut, fetchAuthSession } from 'aws-amplify/auth';
import { logEvent } from "../utils/analytics";
import "../index.css";

// ============================================================================
// Types
// ============================================================================

export interface StandaloneSignUpFormHandle {
  focusEmail: () => void;
}

interface StandaloneSignUpFormProps {
  selectedPlan?: string;
  onSignUpSuccess: (email: string) => void;
  onVerificationSuccess?: () => void;
  onStageChange?: (stage: 'email' | 'verify' | 'password') => void;
}

// ============================================================================
// Helpers
// ============================================================================

const generateTempPassword = (): string => {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const numbers = '0123456789';
  const special = '!@#$%^&*';

  let password = '';
  password += upper[Math.floor(Math.random() * upper.length)];
  password += lower[Math.floor(Math.random() * lower.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += special[Math.floor(Math.random() * special.length)];

  const allChars = upper + lower + numbers + special;
  for (let i = 0; i < 12; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }

  return password.split('').sort(() => Math.random() - 0.5).join('');
};

const clearExistingSession = async () => {
  try {
    const session = await fetchAuthSession({ forceRefresh: true });
    if (session.tokens) {
      await signOut({ global: true });
      localStorage.removeItem('amplify-signin-with-hostedUI');
    }
  } catch {
    // no existing session
  }
};

// ============================================================================
// Component
// ============================================================================

const StandaloneSignUpForm = forwardRef<StandaloneSignUpFormHandle, StandaloneSignUpFormProps>(
  ({ selectedPlan, onSignUpSuccess, onVerificationSuccess, onStageChange }, ref) => {

  // Stage management: 'email' -> 'verify' -> 'password' -> 'complete'
  const [stage, setStage] = useState<'email' | 'verify' | 'password' | 'complete'>('email');

  // Email stage
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [ageAttested, setAgeAttested] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [sessionCleared, setSessionCleared] = useState(false);

  // Verification stage
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [tempPassword, setTempPassword] = useState("");

  // Password stage
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [isSettingPassword, setIsSettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // General
  const [errorMessage, setErrorMessage] = useState("");

  // Refs
  const emailInputRef = useRef<HTMLInputElement>(null);
  const verificationSectionRef = useRef<HTMLDivElement>(null);
  const verificationInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const initialMountRef = useRef(true);

  // ── Expose focusEmail to parent ──────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    focusEmail: () => {
      emailInputRef.current?.focus();
      emailInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    },
  }));

  // Copy tweaks by plan
  const buttonText = selectedPlan === "trial" ? "Start Now" : "Submit Now";
  const emailPlaceholderText =
    selectedPlan === "trial" ? "Enter Your Email to Begin" : "Enter Your Email to Start";

  // Notify parent of stage changes
  useEffect(() => {
    if (stage !== 'complete') {
      onStageChange?.(stage);
    }
  }, [stage, onStageChange]);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Smart scroll on stage change
  useEffect(() => {
    const timer = setTimeout(() => {
      if (stage === 'verify' && verificationInputRef.current) {
        verificationInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        verificationInputRef.current.focus();
      } else if (stage === 'password' && passwordInputRef.current) {
        passwordInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        passwordInputRef.current.focus();
      } else if (stage === 'email') {
        if (initialMountRef.current) {
          initialMountRef.current = false;
          return;
        }
        const heading = document.getElementById("join-free-heading");
        if (heading) {
          const bandEl = document.querySelector(".sticky-header") as HTMLElement | null;
          const bandHeight = bandEl ? bandEl.offsetHeight : 0;
          heading.style.scrollMarginTop = `${bandHeight}px`;
          heading.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [stage]);

  // Validators
  const validateEmail = (input: string) =>
    /\S+@\S+\.\S+/.test(input) ? "" : "Invalid email format";

  const isPasswordValid =
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[!@#$%^&*(),.?":{}|<>]/.test(password);

  const isEmailStageValid = !emailError && email.trim() && termsAccepted && ageAttested;

  // Collapse handler
  const handleCollapse = () => {
    setIsExpanded(false);
    setTermsAccepted(false);
    setAgeAttested(false);
  };

  // STAGE 1: Email submission
  const handleEmailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setEmail(val);
    setEmailError(validateEmail(val));

    if (!isExpanded && !sessionCleared) {
      await clearExistingSession();
      setSessionCleared(true);
    }

    setIsExpanded(true);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isEmailStageValid) {
      setErrorMessage("Please correct errors before continuing.");
      return;
    }

    setErrorMessage("");

    try {
      const tempPass = generateTempPassword();
      setTempPassword(tempPass);

      const result = await signUpUser(email, tempPass);
      if (result?.nextStep?.signUpStep === "CONFIRM_SIGN_UP") {
        setStage('verify');
        onSignUpSuccess(email);
      } else {
        setErrorMessage("Unexpected response from sign-up.");
      }
    } catch (error: any) {
      if (error.name === 'UsernameExistsException') {
        try {
          await resendVerificationCode(email);
          setStage('verify');
          setResendMessage("Account found! We've resent your verification code.");
          onSignUpSuccess(email);
        } catch {
          setErrorMessage("This email is already registered. Please sign in or contact support.");
        }
      } else {
        setErrorMessage("Failed to sign up. Please try again.");
        void ["Sign up error:", error];
      }
    }
  };

  // STAGE 2: Verification
  const handleResendCode = async () => {
    setResendMessage("");
    setVerificationError("");

    try {
      const success = await resendVerificationCode(email);
      if (success) {
        setResendMessage("Code resent! Check your email.");
        setResendCooldown(60);
      } else {
        setResendMessage("Failed to resend code. Please try again.");
      }
    } catch (error) {
      setResendMessage("Failed to resend code. Please try again.");
      void ["Resend error:", error];
    }
  };

  const handleVerification = async () => {
    setVerificationError("");

    if (!verificationCode.trim()) {
      setVerificationError("Please enter the verification code.");
      return;
    }

    setIsVerifying(true);

    try {
      const success = await confirmSignUpUser(email, verificationCode);
      if (success) {
        try {
          await signOut({ global: true });
        } catch {
          // no active session
        }
        setStage('password');
        setVerificationError("");
      } else {
        setVerificationError("Invalid code. Please try again.");
      }
    } catch (error) {
      setVerificationError("Invalid code. Please try again.");
      void ["Verification error:", error];
    } finally {
      setIsVerifying(false);
    }
  };

  // STAGE 3: Password creation
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    setPasswordTouched(true);
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setConfirmPassword(val);
    setConfirmPasswordError(val === password ? "" : "Passwords do not match");
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid || confirmPassword !== password) {
      setErrorMessage("Please correct errors before continuing.");
      return;
    }

    setIsSettingPassword(true);
    setErrorMessage("");

    try {
      await signInUser(email, tempPassword);

      const { updatePassword } = await import('aws-amplify/auth');
      await updatePassword({ oldPassword: tempPassword, newPassword: password });

      await signOut({ global: true });
      await signInUser(email, password);

      await new Promise(resolve => setTimeout(resolve, 1500));

      const session = await fetchAuthSession({ forceRefresh: true });
      if (session.tokens) {
        logEvent("user.joined");
        if (onVerificationSuccess) {
          onVerificationSuccess();
        }
        setStage('complete');
      } else {
        throw new Error('Session not ready after sign-in');
      }
    } catch (error: any) {
      void ["Password setup error:", error];
      if (error.name === 'NotAuthorizedException') {
        setErrorMessage("Authentication failed. Please try signing in manually.");
      } else if (error.name === 'InvalidPasswordException') {
        setErrorMessage("Password doesn't meet requirements. Please try a different password.");
      } else {
        setErrorMessage("Failed to set password. Please try again or contact support.");
      }
    } finally {
      setIsSettingPassword(false);
    }
  };

  // ── Eye icon SVGs ─────────────────────────────────────────────────────────
  const EyeOff = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  );
  const EyeOn = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="relative">

      {/* Processing Overlay */}
      {isSettingPassword && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center">
          <div className="p-8 m-4">
            <div className="animate-spin zodiyak-spinner rounded-full h-80 w-80" aria-hidden="true" />
          </div>
        </div>
      )}

      {/* ── STAGE 1: Email Collection ── */}
      {stage === 'email' && (
        <form onSubmit={handleEmailSubmit} noValidate style={{ width: "100%" }}>
          <div className="field relative" style={{ position: "relative" }}>
            <input
              ref={emailInputRef}
              type="email"
              value={email}
              onChange={handleEmailChange}
              onFocus={() => {
                setIsExpanded(true);
                setTimeout(() => {
                  const card = document.getElementById("plan-card");
                  if (!card) return;
                  const bandEl = document.querySelector(".sticky-header") as HTMLElement | null;
                  const bandHeight = bandEl ? bandEl.offsetHeight : 0;
                  card.style.scrollMarginTop = `${bandHeight}px`;
                  card.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 300);
              }}
              placeholder={emailPlaceholderText}
              autoComplete="email"
              aria-invalid={!!emailError}
              style={{
                width: "100%",
                border: "2px solid #E05A3A",
                borderRadius: 100,
                padding: "14px 20px",
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 16,
                outline: "none",
                background: "#FFFFFF",
                color: "#1A1A1A",
                boxSizing: "border-box",
                boxShadow: "none",
              }}
            />
            {isExpanded && (
              <button
                type="button"
                onClick={handleCollapse}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors z-10"
                aria-label="Collapse form"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {emailError && <p className="form-error">{emailError}</p>}
          </div>

          {isExpanded && (
            <>
              {/* Terms & Conditions */}
              <div className="checkbox-row pt-4">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={() => setTermsAccepted(!termsAccepted)}
                />
                <label className="px-3 text-left" htmlFor="terms">
                  I have read and agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                    Terms & Conditions
                  </a>
                  ,{" "}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                    Privacy Policy
                  </a>{" "}
                  and{" "}
                  <a href="/cookies" target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }}>
                    Cookie Policy.
                  </a>
                </label>
              </div>

              {/* Age attestation */}
              <div className="checkbox-row pt-2">
                <input
                  type="checkbox"
                  id="age-attest"
                  checked={ageAttested}
                  onChange={() => setAgeAttested(!ageAttested)}
                />
                <label className="px-3 text-left" htmlFor="age-attest">
                  I confirm I am 18 years of age or older.
                </label>
              </div>

              <div className="py-4 px-4 mt-1 mb-2 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-base text-gray-800 leading-relaxed">
                  <strong className="text-gray-900">Your Privacy Matters.</strong> Crushhh is built
                  on anonymity and trust. We never share, sell, or expose your data. We're proudly different.
                </p>
              </div>

              {errorMessage && <p className="form-error">{errorMessage}</p>}

              <button
                type="submit"
                disabled={!isEmailStageValid}
                title={!isEmailStageValid ? "Please correct the missing or invalid information" : ""}
                style={{
                  background: "#E05A3A",
                  color: "#FFFFFF",
                  border: "3px solid #1A1A1A",
                  borderRadius: 100,
                  boxShadow: "4px 4px 0px #1A1A1A",
                  padding: "14px 36px",
                  fontFamily: "'Fredoka', sans-serif",
                  fontSize: 18,
                  fontWeight: 700,
                  width: "100%",
                  marginTop: 12,
                  cursor: isEmailStageValid ? "pointer" : "not-allowed",
                  opacity: isEmailStageValid ? 1 : 0.5,
                  transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
                }}
              >
                Continue
              </button>
            </>
          )}

          {!isExpanded && (
            <button
              type="button"
              onClick={() => {
                setIsExpanded(true);
                emailInputRef.current?.focus();
              }}
              style={{
                background: "#E05A3A",
                color: "#FFFFFF",
                border: "3px solid #1A1A1A",
                borderRadius: 100,
                boxShadow: "4px 4px 0px #1A1A1A",
                padding: "14px 36px",
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 18,
                fontWeight: 700,
                width: "100%",
                marginTop: 12,
                cursor: "pointer",
                transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
              }}
            >
              Continue
            </button>
          )}
        </form>
      )}

      {/* ── STAGE 2: Email Verification ── */}
      {stage === 'verify' && (
        <div ref={verificationSectionRef} style={{ marginTop: 4 }}>
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 14,
                color: "#1A1A1A",
                margin: 0,
              }}
            >
              We've sent a verification code to<br />
              <strong>{email}</strong>
            </p>
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 13,
                color: "#555",
                marginTop: 8,
                marginBottom: 0,
              }}
            >
              Enter the code below
            </p>
          </div>

          <input
            ref={verificationInputRef}
            type="text"
            value={verificationCode}
            onChange={(e) => {
              setVerificationCode(e.target.value);
              setVerificationError("");
              setResendMessage("");
            }}
            placeholder="Enter verification code"
            disabled={isVerifying}
            style={{
              width: "100%",
              border: "2.5px solid #E05A3A",
              borderRadius: 100,
              padding: "14px 20px",
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 16,
              background: "#FDF6EE",
              color: "#1A1A1A",
              outline: "none",
              boxSizing: "border-box",
              boxShadow: "none",
              opacity: isVerifying ? 0.6 : 1,
              cursor: isVerifying ? "not-allowed" : "text",
            }}
          />

          {verificationError && (
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 13,
                color: "#C44B2E",
                textAlign: "center",
                marginTop: 8,
                marginBottom: 0,
              }}
            >
              {verificationError}
            </p>
          )}
          {resendMessage && (
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 13,
                color: "#E05A3A",
                textAlign: "center",
                marginTop: 8,
                marginBottom: 0,
              }}
            >
              {resendMessage}
            </p>
          )}

          <button
            type="button"
            onClick={handleVerification}
            disabled={isVerifying || !verificationCode.trim()}
            style={{
              background: "#E05A3A",
              color: "#FFFFFF",
              border: "3px solid #1A1A1A",
              borderRadius: 100,
              boxShadow: "4px 4px 0px #1A1A1A",
              padding: "14px 36px",
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 18,
              fontWeight: 700,
              width: "100%",
              marginTop: 12,
              cursor: isVerifying || !verificationCode.trim() ? "not-allowed" : "pointer",
              opacity: isVerifying || !verificationCode.trim() ? 0.5 : 1,
              boxSizing: "border-box",
              transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
            }}
          >
            {isVerifying ? 'Verifying...' : 'Verify Email'}
          </button>

          <p
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: 13,
              color: "#888",
              textAlign: "center",
              marginTop: 16,
              marginBottom: 0,
            }}
          >
            Didn't receive the code?{" "}
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || isVerifying}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                fontFamily: "'Nunito', sans-serif",
                fontSize: 13,
                color: "#E05A3A",
                fontWeight: 700,
                cursor: (resendCooldown > 0 || isVerifying) ? 'not-allowed' : 'pointer',
                opacity: (resendCooldown > 0 || isVerifying) ? 0.5 : 1,
              }}
            >
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
            </button>
          </p>
        </div>
      )}

      {/* ── STAGE 3: Password Creation ── */}
      {stage === 'password' && (
        <form onSubmit={handlePasswordSubmit} noValidate style={{ width: "100%" }}>
          <p
            style={{
              fontFamily: "'Nunito', sans-serif",
              fontSize: 13,
              color: "#555",
              textAlign: "center",
              marginBottom: 16,
              marginTop: 0,
            }}
          >
            Choose a secure password for your account
          </p>

          {/* Password field */}
          <div style={{ position: "relative", marginBottom: 12 }}>
            <input
              ref={passwordInputRef}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={handlePasswordChange}
              onFocus={() => setPasswordTouched(true)}
              placeholder="Create a password"
              autoComplete="new-password"
              disabled={isSettingPassword}
              style={{
                width: "100%",
                border: "2.5px solid #E05A3A",
                borderRadius: 100,
                padding: "14px 48px 14px 20px",
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 16,
                background: "#FDF6EE",
                color: "#1A1A1A",
                outline: "none",
                boxSizing: "border-box",
                boxShadow: "none",
                opacity: isSettingPassword ? 0.6 : 1,
                cursor: isSettingPassword ? "not-allowed" : "text",
              }}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-0"
              aria-label={showPassword ? "Hide password" : "Show password"}
              disabled={isSettingPassword}
              style={{ top: "calc(50% - 6px)" }}
            >
              {showPassword ? <EyeOff /> : <EyeOn />}
            </button>
          </div>

          {/* Password requirements */}
          {passwordTouched && !isPasswordValid && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                marginBottom: 12,
                paddingLeft: 8,
              }}
            >
              {[
                { text: "8+ characters",     ok: password.length >= 8 },
                { text: "Uppercase letter",  ok: /[A-Z]/.test(password) },
                { text: "Lowercase letter",  ok: /[a-z]/.test(password) },
                { text: "Number",            ok: /[0-9]/.test(password) },
                { text: "Special character", ok: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
              ].map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontFamily: "'Nunito', sans-serif",
                    fontSize: 13,
                    color: item.ok ? "#1A1A1A" : "#888",
                  }}
                >
                  <span
                    style={{
                      width: 16,
                      textAlign: "center",
                      color: item.ok ? "#E05A3A" : "#888",
                      fontWeight: 700,
                    }}
                  >
                    {item.ok ? "✓" : "✗"}
                  </span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          )}

          {/* Confirm password field */}
          <div style={{ position: "relative", marginBottom: 12 }}>
            <input
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              placeholder="Confirm password"
              autoComplete="new-password"
              aria-invalid={!!confirmPasswordError}
              disabled={isSettingPassword}
              style={{
                width: "100%",
                border: "2.5px solid #E05A3A",
                borderRadius: 100,
                padding: "14px 48px 14px 20px",
                fontFamily: "'Fredoka', sans-serif",
                fontSize: 16,
                background: "#FDF6EE",
                color: "#1A1A1A",
                outline: "none",
                boxSizing: "border-box",
                boxShadow: "none",
                opacity: isSettingPassword ? 0.6 : 1,
                cursor: isSettingPassword ? "not-allowed" : "text",
              }}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-0"
              aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              disabled={isSettingPassword}
              style={{ top: "calc(50% - 6px)" }}
            >
              {showConfirmPassword ? <EyeOff /> : <EyeOn />}
            </button>
          </div>
          {confirmPasswordError && (
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 13,
                color: "#C44B2E",
                textAlign: "center",
                marginTop: 0,
                marginBottom: 8,
              }}
            >
              {confirmPasswordError}
            </p>
          )}

          {errorMessage && (
            <p
              style={{
                fontFamily: "'Nunito', sans-serif",
                fontSize: 13,
                color: "#C44B2E",
                textAlign: "center",
                marginTop: 0,
                marginBottom: 8,
              }}
            >
              {errorMessage}
            </p>
          )}

          <button
            type="submit"
            disabled={isSettingPassword || !isPasswordValid || confirmPassword !== password}
            style={{
              background: "#E05A3A",
              color: "#FFFFFF",
              border: "3px solid #1A1A1A",
              borderRadius: 100,
              boxShadow: "4px 4px 0px #1A1A1A",
              padding: "14px 36px",
              fontFamily: "'Fredoka', sans-serif",
              fontSize: 18,
              fontWeight: 700,
              width: "100%",
              marginTop: 4,
              cursor:
                isSettingPassword || !isPasswordValid || confirmPassword !== password
                  ? "not-allowed"
                  : "pointer",
              opacity:
                isSettingPassword || !isPasswordValid || confirmPassword !== password ? 0.5 : 1,
              boxSizing: "border-box",
              transition: "transform 0.12s ease, box-shadow 0.12s ease, background 0.12s ease",
            }}
          >
            {isSettingPassword ? 'Setting up your account...' : buttonText}
          </button>
        </form>
      )}
    </div>
  );
});

StandaloneSignUpForm.displayName = 'StandaloneSignUpForm';

export default StandaloneSignUpForm;